/**
 * Sponsors Management - Platform admin page for managing sponsors
 * Premium dark mode design
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Building,
  Plus,
  Search,
  ExternalLink,
  Mail,
  Phone,
  DollarSign,
  Calendar,
  Edit2,
  Trash2,
  Award,
  TrendingUp,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tier badge styles
const tierStyles = {
  bronze: 'bg-amber-700/20 text-amber-600 border-amber-700/30',
  silver: 'bg-slate-300/20 text-slate-300 border-slate-300/30',
  gold: 'bg-soft-amber/20 text-soft-amber border-soft-amber/30',
  platinum: 'bg-cyan-400/20 text-cyan-400 border-cyan-400/30',
  custom: 'bg-purple-400/20 text-purple-400 border-purple-400/30',
};

// Glass panel component
const GlassPanel = ({ children, className = '', ...props }) => (
  <div
    className={`bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] rounded-2xl ${className}`}
    {...props}
  >
    {children}
  </div>
);

// Sponsor card
const SponsorCard = ({ sponsor, onEdit, onDelete }) => {
  const tierStyle = tierStyles[sponsor.tier] || tierStyles.bronze;

  return (
    <GlassPanel className="p-5 hover:border-white/[0.12] transition-all duration-300">
      <div className="flex items-start gap-4">
        {/* Logo */}
        <div className="w-16 h-16 rounded-xl bg-white/[0.05] flex items-center justify-center flex-shrink-0 overflow-hidden">
          {sponsor.logo_url ? (
            <img src={sponsor.logo_url} alt={sponsor.name} className="w-full h-full object-contain p-2" />
          ) : (
            <Building className="w-8 h-8 text-muted-text" />
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="font-medium text-cloud-white">{sponsor.name}</h3>
              <Badge className={`mt-1 ${tierStyle} border`}>
                {sponsor.tier?.toUpperCase()}
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onEdit(sponsor)}>
                <Edit2 className="w-4 h-4 text-muted-text hover:text-cloud-white" />
              </Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onDelete(sponsor)}>
                <Trash2 className="w-4 h-4 text-muted-text hover:text-signal-red" />
              </Button>
            </div>
          </div>

          {sponsor.description && (
            <p className="text-sm text-muted-text mt-2 line-clamp-2">{sponsor.description}</p>
          )}

          {/* Contact Info */}
          <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-soft-silver">
            {sponsor.contact_email && (
              <span className="flex items-center gap-1">
                <Mail className="w-3 h-3" /> {sponsor.contact_email}
              </span>
            )}
            {sponsor.website_url && (
              <a 
                href={sponsor.website_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-electric-ice-blue hover:underline"
              >
                <ExternalLink className="w-3 h-3" /> Website
              </a>
            )}
          </div>

          {/* Contract Info */}
          {sponsor.contract_value > 0 && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-white/[0.06] text-xs">
              <span className="flex items-center gap-1 text-cool-mint">
                <DollarSign className="w-3 h-3" />
                ${sponsor.contract_value.toLocaleString()}
              </span>
              {sponsor.contract_end_date && (
                <span className="flex items-center gap-1 text-muted-text">
                  <Calendar className="w-3 h-3" />
                  Ends {new Date(sponsor.contract_end_date).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </GlassPanel>
  );
};

// Create/Edit sponsor form
const SponsorForm = ({ sponsor, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: sponsor?.name || '',
    slug: sponsor?.slug || '',
    logo_url: sponsor?.logo_url || '',
    website_url: sponsor?.website_url || '',
    description: sponsor?.description || '',
    contact_name: sponsor?.contact_name || '',
    contact_email: sponsor?.contact_email || '',
    contact_phone: sponsor?.contact_phone || '',
    tier: sponsor?.tier || 'bronze',
    contract_value: sponsor?.contract_value || 0,
    contract_start_date: sponsor?.contract_start_date || '',
    contract_end_date: sponsor?.contract_end_date || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 py-4">
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Sponsor Name *</Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
          />
        </div>

        <div>
          <Label htmlFor="tier">Tier</Label>
          <Select value={form.tier} onValueChange={(v) => setForm({ ...form, tier: v })}>
            <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/[0.1]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bronze">Bronze</SelectItem>
              <SelectItem value="silver">Silver</SelectItem>
              <SelectItem value="gold">Gold</SelectItem>
              <SelectItem value="platinum">Platinum</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
            rows={3}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="logo_url">Logo</Label>
            <div className="mt-1.5 space-y-2">
              <Input
                id="logo_url"
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                className="bg-white/[0.03] border-white/[0.1]"
                placeholder="https://... or upload below"
              />
              <div className="flex items-center gap-2">
                <Label htmlFor="logo_upload" className="flex-1 cursor-pointer">
                  <div className="flex items-center justify-center gap-2 px-3 py-2 bg-white/[0.03] border border-dashed border-white/[0.1] rounded-lg hover:border-electric-ice-blue/50 transition-colors">
                    <Upload className="w-4 h-4 text-muted-text" />
                    <span className="text-sm text-muted-text">Upload Logo</span>
                  </div>
                  <input
                    id="logo_upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('File must be less than 5MB');
                        return;
                      }
                      // Convert to base64 data URL for preview
                      const reader = new FileReader();
                      reader.onload = () => {
                        setForm({ ...form, logo_url: reader.result });
                        toast.success('Logo uploaded - will be stored when you save');
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                </Label>
                {form.logo_url && (
                  <div className="w-10 h-10 rounded bg-white/[0.05] overflow-hidden flex-shrink-0">
                    <img src={form.logo_url} alt="Preview" className="w-full h-full object-contain" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="website_url">Website URL</Label>
            <Input
              id="website_url"
              value={form.website_url}
              onChange={(e) => setForm({ ...form, website_url: e.target.value })}
              className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              placeholder="https://..."
            />
          </div>
        </div>

        <div className="pt-4 border-t border-white/[0.06]">
          <h4 className="text-sm font-medium text-cloud-white mb-3">Contact Information</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact_name">Contact Name</Label>
              <Input
                id="contact_name"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
            <div>
              <Label htmlFor="contact_email">Email</Label>
              <Input
                id="contact_email"
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-white/[0.06]">
          <h4 className="text-sm font-medium text-cloud-white mb-3">Contract Details</h4>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="contract_value">Value ($)</Label>
              <Input
                id="contract_value"
                type="number"
                value={form.contract_value}
                onChange={(e) => setForm({ ...form, contract_value: parseFloat(e.target.value) || 0 })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
            <div>
              <Label htmlFor="contract_start_date">Start Date</Label>
              <Input
                id="contract_start_date"
                type="date"
                value={form.contract_start_date}
                onChange={(e) => setForm({ ...form, contract_start_date: e.target.value })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
            <div>
              <Label htmlFor="contract_end_date">End Date</Label>
              <Input
                id="contract_end_date"
                type="date"
                value={form.contract_end_date}
                onChange={(e) => setForm({ ...form, contract_end_date: e.target.value })}
                className="mt-1.5 bg-white/[0.03] border-white/[0.1]"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-white/[0.06]">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving...' : sponsor ? 'Update Sponsor' : 'Create Sponsor'}
        </Button>
      </div>
    </form>
  );
};

export const Sponsors = () => {
  const { session, profile } = useAuth();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);

  // Check if user is platform admin
  const isPlatformAdmin = ['platform_owner', 'platform_admin'].includes(profile?.platform_role);

  // Fetch sponsors
  const fetchSponsors = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/sponsors?limit=100`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setSponsors(data.sponsors || []);
      } else {
        console.warn('Sponsors API returned', res.status);
      }
    } catch (err) {
      console.error('Failed to fetch sponsors:', err);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchSponsors();
  }, [fetchSponsors]);

  // Create/Update sponsor
  const handleSaveSponsor = async (formData) => {
    try {
      const url = editingSponsor
        ? `${API_URL}/api/sponsors/${editingSponsor.id}`
        : `${API_URL}/api/sponsors`;
      
      const res = await fetch(url, {
        method: editingSponsor ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        toast.success(editingSponsor ? 'Sponsor updated' : 'Sponsor created');
        fetchSponsors();
        setIsFormOpen(false);
        setEditingSponsor(null);
      } else {
        toast.error('Failed to save sponsor');
      }
    } catch (err) {
      console.error('Failed to save sponsor:', err);
      toast.error('Failed to save sponsor');
    }
  };

  // Delete sponsor
  const handleDeleteSponsor = async (sponsor) => {
    if (!confirm(`Delete sponsor "${sponsor.name}"?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/sponsors/${sponsor.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.ok) {
        toast.success('Sponsor deactivated');
        fetchSponsors();
      }
    } catch (err) {
      console.error('Failed to delete sponsor:', err);
    }
  };

  // Filter sponsors
  const filteredSponsors = sponsors.filter((s) =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats
  const totalValue = sponsors.reduce((sum, s) => sum + (s.contract_value || 0), 0);
  const activeTiers = [...new Set(sponsors.map((s) => s.tier))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-electric-ice-blue border-t-transparent rounded-full animate-spin" />
          <p className="text-soft-silver text-sm">Loading sponsors...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="sponsors-page" className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-cloud-white flex items-center gap-3">
            <Award className="w-8 h-8 text-soft-amber" />
            Sponsors
          </h1>
          <p className="text-soft-silver mt-1">Manage sponsorship partnerships</p>
        </div>

        {isPlatformAdmin && (
          <Button onClick={() => { setEditingSponsor(null); setIsFormOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Add Sponsor
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassPanel className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-text text-sm">Total Sponsors</p>
              <p className="text-2xl font-mono font-bold text-cloud-white mt-1">{sponsors.length}</p>
            </div>
            <Building className="w-8 h-8 text-electric-ice-blue" />
          </div>
        </GlassPanel>
        <GlassPanel className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-text text-sm">Contract Value</p>
              <p className="text-2xl font-mono font-bold text-cool-mint mt-1">
                ${totalValue.toLocaleString()}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-cool-mint" />
          </div>
        </GlassPanel>
        <GlassPanel className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-text text-sm">Active Tiers</p>
              <p className="text-2xl font-mono font-bold text-soft-amber mt-1">{activeTiers.length}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-soft-amber" />
          </div>
        </GlassPanel>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-text" />
        <Input
          placeholder="Search sponsors..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white/[0.03] border-white/[0.1]"
        />
      </div>

      {/* Sponsors Grid */}
      {filteredSponsors.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredSponsors.map((sponsor) => (
            <SponsorCard
              key={sponsor.id}
              sponsor={sponsor}
              onEdit={(s) => { setEditingSponsor(s); setIsFormOpen(true); }}
              onDelete={handleDeleteSponsor}
            />
          ))}
        </div>
      ) : (
        <GlassPanel className="p-12 text-center">
          <Building className="w-12 h-12 text-muted-text mx-auto mb-4" />
          <h3 className="text-lg font-medium text-cloud-white mb-2">No Sponsors Yet</h3>
          <p className="text-muted-text mb-4">
            Add sponsors to start managing partnerships.
          </p>
          {isPlatformAdmin && (
            <Button onClick={() => setIsFormOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> Add First Sponsor
            </Button>
          )}
        </GlassPanel>
      )}

      {/* Form Sheet */}
      <Sheet open={isFormOpen} onOpenChange={setIsFormOpen}>
        <SheetContent className="bg-graphite border-white/[0.06] w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-cloud-white">
              {editingSponsor ? 'Edit Sponsor' : 'Add Sponsor'}
            </SheetTitle>
            <SheetDescription className="text-muted-text">
              {editingSponsor ? 'Update sponsor details' : 'Create a new sponsor partnership'}
            </SheetDescription>
          </SheetHeader>
          <SponsorForm
            sponsor={editingSponsor}
            onSave={handleSaveSponsor}
            onClose={() => { setIsFormOpen(false); setEditingSponsor(null); }}
          />
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Sponsors;
